import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AiDocumentationFieldEnum,
  AiFieldDetectionStatusEnum,
  DocumentationValidationStatusEnum,
} from 'graphql-common';
import { TabBarItem as TabBarItemType } from '@lib/components/TabBar/constants';
import TabBar from '@lib/components/TabBar/TabBar';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import DashboardContentWithRightSidebar from '@lib/layouts/DashboardLayout/DashboardContentWithRightSidebar/DashboardContentWithRightSidebar';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import ActionsMenu from '@lib/components/ActionsMenu/ActionsMenu';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import { NavItemTypeEnum } from '@lib/components/Menu/Menu';
import Icons from '@lib/components/Icons/Icons';
import DocumentVersionNotes from '@lib/components/DocumentationPreview/DocumentVersionNotes';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import { materialSymbolsOutlined } from '@lib/enums/common';
import useTabParam from '@lib/hooks/useTabParam';
import useEdmDocumentationQueryHook from 'utils/fetch/hooks/useEdmDocumentationQueryHook';
import useDocumentDetectionRefetch from 'routes/Documentation/utils/useDocumentDetectionRefetch';
import useAiBudgetStatus from 'components/AiAssistant/hooks/useAiBudgetStatus';
import AiFieldAssist from 'routes/Documentation/components/AiDetection/AiFieldAssist';
import { AiFieldUiState } from 'routes/Documentation/utils/getAiFieldDetectionState';
import DocumentInfo from './DocumentInfo/DocumentInfo';
import DocumentViewAccesses from './DocumentViewAccesses/DocumentViewAccesses';
import DocumentViewDetails from './DocumentViewDetails/DocumentViewDetails';
import DocumentViewHistory from './DocumentViewHistory/DocumentViewHistory';
import DocumentViewOperationalScope from './DocumentViewOperationalScope/DocumentViewOperationalScope';
import getDocumentViewPageTabs from './utils/getDocumentViewPageTabs';
import useDocumentFieldDetection from './utils/useDocumentFieldDetection';

const TABS_WITH_SIDEBAR = ['details', 'accesses', 'history'];

type Props = {
  documentQueryHookResult: ReturnType<typeof useEdmDocumentationQueryHook>;
  isAccessesAvailable: boolean;
  isAssetsAvailable: boolean;
  isFavorited: boolean;
  isFavoriteLoading: boolean;
  isFullAdmin?: boolean;
  isOperationalScopeAvailable: boolean;
  isSitesAvailable: boolean;
  isTasksAvailable: boolean;
  isViewLocked: boolean;
  isViewOnly: boolean;
  isAiDetectionEnabled: boolean;
  loading: boolean;
  onCreateVersion: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onFavoriteToggle: () => void;
  onSkipRenewal: () => void;
  onValidationStatusChange: (
    id: string,
    newStatus: DocumentationValidationStatusEnum,
  ) => void;
  validationStatusLoading: boolean;
  activityRefetchKey: number;
};

export default function DocumentViewPage({
  documentQueryHookResult,
  isAccessesAvailable,
  isAssetsAvailable,
  isFavorited,
  isFavoriteLoading,
  isFullAdmin,
  isOperationalScopeAvailable,
  isSitesAvailable,
  isTasksAvailable,
  isViewLocked,
  isViewOnly,
  isAiDetectionEnabled,
  loading,
  onCreateVersion,
  onEdit,
  onDelete,
  onFavoriteToggle,
  onSkipRenewal,
  onValidationStatusChange,
  validationStatusLoading,
  activityRefetchKey,
}: Props) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useTabParam({
    defaultTab: 'details',
    usePagination: false,
  });
  const [isVersionNotesOpen, setIsVersionNotesOpen] = useState(false);
  const { data, refetch } = documentQueryHookResult;

  const isDetecting =
    isAiDetectionEnabled &&
    (data?.aiDetectionStatus || []).some(
      (entry) =>
        entry.status === AiFieldDetectionStatusEnum.Queued ||
        entry.status === AiFieldDetectionStatusEnum.Detecting,
    );

  const { credits } = useAiBudgetStatus(isAiDetectionEnabled);
  const aiDetection = useDocumentFieldDetection({
    doc: data,
    documentationVersionId: data?.currentVersion?.id,
    isAiEnabled: isAiDetectionEnabled,
  });
  useDocumentDetectionRefetch({ isDetecting, refetch });

  const tabs = useMemo(
    () =>
      getDocumentViewPageTabs({
        t,
        isAccessesAvailable,
        isOperationalScopeAvailable,
      }),
    [t, isAccessesAvailable, isOperationalScopeAvailable],
  );
  const useSidebar = TABS_WITH_SIDEBAR.includes(activeTab || '');

  // If the URL points at a tab the user can't see (e.g. ?tab=accesses but
  // SitesManagement is LimitedRead), reset to the default visible tab.
  useEffect(() => {
    if (!activeTab) return;
    if (tabs.some((tab) => tab.value === activeTab)) return;
    setActiveTab('details');
  }, [activeTab, tabs, setActiveTab]);

  const canUpdate = data?.canUpdate?.value;
  const canDestroy = data?.canDestroy?.value;
  const versionNotes = data?.currentVersion?.notes || '';
  const hasVersionNotes = hasTextEditorValue(versionNotes);

  const actionsMenu = [
    {
      id: 'version-notes',
      title: t('version-notes'),
      onClick: () => setIsVersionNotesOpen(true),
      leftIcon: Icons.article,
      type: NavItemTypeEnum.Secondary,
      predictorFnDisable: () => !hasVersionNotes,
    },
    {
      id: 'edit-action',
      title: t('edit'),
      onClick: onEdit,
      leftIcon: Icons.edit,
      type: NavItemTypeEnum.Secondary,
      predictorFnVisible: () => Boolean(canUpdate),
    },
  ];

  const pageBody = (
    <PageBody withoutHeader>
      <TabBar
        items={tabs}
        active={activeTab}
        onActiveChange={({ value }: TabBarItemType) => setActiveTab(value)}
      />
      <CircleLoaderWrapper
        isLoading={loading}
        type={CircleLoaderWrapperType.blur}
      >
        <WhiteBox>
          <DashboardTitleHeader
            title={
              isAiDetectionEnabled &&
              aiDetection.name.uiState !== AiFieldUiState.None ? (
                <AiFieldAssist
                  field={AiDocumentationFieldEnum.Name}
                  resolveSource={aiDetection.name.resolveSource}
                  onApplyValue={aiDetection.name.onApplyValue}
                  detectionUiState={aiDetection.name.uiState}
                  detectingLabel={t('ai-generating-name')}
                  onDismiss={aiDetection.name.onDismiss}
                  creditsRemaining={credits}
                  labels={{
                    idleLabel: t('ai-generate-with-ai'),
                    idleCta: t('ai-generate-name-cta'),
                    regenerateCta: t('ai-regenerate'),
                  }}
                />
              ) : (
                data?.name
              )
            }
            actions={[
              // Favorite toggle is a premium affordance — hide for
              // non-Full-Admin Users on a locked plan since they can't
              // act on the favorites feature anyway.
              ...(isViewLocked && !isFullAdmin
                ? []
                : [
                    <Tooltip
                      key="favorite"
                      body={t('unlock-favorites-tooltip')}
                      disabled={!isViewLocked}
                    >
                      <IconButton
                        icon={isFavorited ? 'bookmark_star' : 'bookmark'}
                        onClick={onFavoriteToggle}
                        symbolsOutlined
                        filled={isFavorited}
                        isLoading={isFavoriteLoading}
                        type={IconButtonTypes.PrimaryOutlined}
                        disabled={isViewLocked}
                      />
                    </Tooltip>,
                  ]),
              <ActionsMenu
                key="actions-menu"
                actions={actionsMenu}
                disabled={loading}
              />,
            ]}
          />
          {activeTab === 'details' && (
            <DocumentViewDetails
              documentQueryHookResult={documentQueryHookResult}
              isFavorited={isFavorited}
              isFavoriteLoading={isFavoriteLoading}
              isViewOnly={isViewOnly}
              onCreateVersion={onCreateVersion}
              onFavoriteToggle={onFavoriteToggle}
              onSkipRenewal={onSkipRenewal}
              descriptionDetection={
                isAiDetectionEnabled ? aiDetection.description : undefined
              }
              tagsDetection={
                isAiDetectionEnabled ? aiDetection.tags : undefined
              }
              credits={credits}
            />
          )}
          {activeTab === 'accesses' && isAccessesAvailable && data?.id && (
            <DocumentViewAccesses
              documentId={data.id}
              isSitesAvailable={isSitesAvailable}
              isViewLocked={isViewLocked}
            />
          )}
          {activeTab === 'operationalScope' &&
            isOperationalScopeAvailable &&
            data?.id && (
              <DocumentViewOperationalScope
                documentId={data.id}
                isAssetsAvailable={isAssetsAvailable}
                isFullAdmin={isFullAdmin}
                isSitesAvailable={isSitesAvailable}
                isTasksAvailable={isTasksAvailable}
                isViewLocked={isViewLocked}
              />
            )}
          {activeTab === 'history' && (
            <DocumentViewHistory
              documentQueryHookResult={documentQueryHookResult}
              isFullAdmin={isFullAdmin}
              isViewLocked={isViewLocked}
              activityRefetchKey={activityRefetchKey}
            />
          )}
        </WhiteBox>
        {activeTab === 'details' && canDestroy && (
          <div className={entityViewStyles.actions}>
            <Button
              buttonText={t('delete-document')}
              buttonType={ButtonTypes.negativeOutlined}
              leftIcon="delete"
              leftIconClassName={materialSymbolsOutlined}
              onClick={onDelete}
            />
          </div>
        )}
      </CircleLoaderWrapper>
    </PageBody>
  );

  const versionNotesModal = (
    <DocumentVersionNotes
      isOpen={isVersionNotesOpen}
      notes={versionNotes}
      onClose={() => setIsVersionNotesOpen(false)}
    />
  );

  const sidebar = (
    <DocumentInfo
      documentQueryHookResult={documentQueryHookResult}
      isViewLocked={isViewLocked}
      loading={loading}
      onValidationStatusChange={onValidationStatusChange}
      validationStatusLoading={validationStatusLoading}
      codeDetection={isAiDetectionEnabled ? aiDetection.code : undefined}
      categoryDetection={
        isAiDetectionEnabled ? aiDetection.category : undefined
      }
      credits={credits}
    />
  );

  // The layout stays mounted across every tab and only drops its sidebar
  // column, rather than being swapped for a bare `pageBody`. Swapping it
  // remounts everything underneath — including the TabBar, whose sliding
  // active indicator can't animate across a remount.
  return (
    <>
      <DashboardContentWithRightSidebar
        sidebar={sidebar}
        useSidebarHeader
        toggleBtnTop={62.5}
        withoutSidebar={!useSidebar}
      >
        {pageBody}
      </DashboardContentWithRightSidebar>
      {versionNotesModal}
    </>
  );
}
